import { EntityState, createEntityAdapter } from "@ngrx/entity";
import { on, createReducer } from "@ngrx/store";
import { LineItemModelType } from "../insurance-policy-benefits/line-item-model-type";
import {
  TreatmentPlanApiActions,
  TreatmentPageActions,
  ImaxxActions,
} from "../actions/action-index";
import { flattenFees, mapLineItems } from "../insurance-policy-benefits/maps";
import { DhmoLineItem } from "../insurance-policy-benefits/dhmo-line-item";

export const featureKey = "lineItems";

export type LineItemState = EntityState<LineItemModelType>;

export const adapter = createEntityAdapter<LineItemModelType>({
  selectId: (lineItem) => lineItem.id,
});

export const initialState: LineItemState = adapter.getInitialState();

export const reducer = createReducer(
  initialState,
  on(TreatmentPlanApiActions.loadBenefitSuccess, (state, { benefits }) => {
    return adapter.setAll(
      benefits
        .map((b) =>
          flattenFees(
            mapLineItems(
              b.InsurancePolicyBenefits.reduce(
                (pv, cv) => pv.concat(...cv.LineItems),
                []
              )
            )
          )
        )
        .reduce((pv, cv) => pv.concat(...cv), []),
      state
    );
  }),
  on(TreatmentPlanApiActions.calculateSuccess, (state, { benefit }) => {
    // Extract line items from each insurance policy benefit
    const lineItems = benefit.InsurancePolicyBenefits.flatMap(
      (benefit) => benefit.LineItems
    );

    // Map and flatten fees
    const fees = flattenFees(mapLineItems(lineItems));

    // Get IDs of insurance policy benefits
    const insurancePolicyBenefitIds = benefit.InsurancePolicyBenefits.map(
      (benefit) => benefit.Id
    );

    // Get keys to remove from state
    const keysToRemove = Object.keys(state.entities)
      .filter((key) =>
        insurancePolicyBenefitIds.includes(
          state.entities[key].insurancePolicyBenefitId
        )
      )
      .map(Number);

    // Remove old fees and add new ones
    const newState = adapter.removeMany(keysToRemove, state);
    return adapter.addMany(fees, newState);
  }),
  on(
    ImaxxActions.updateImaxxOptimizerSuccess,
    (state, { TreatmentPlanBenefit }) => {
      if (!TreatmentPlanBenefit) {
        return state;
      }

      // Extract line items from each insurance policy benefit
      const lineItems = TreatmentPlanBenefit.InsurancePolicyBenefits.flatMap(
        (benefit) => benefit.LineItems
      );

      // Map and flatten fees
      const fees = flattenFees(mapLineItems(lineItems));

      // Get IDs of insurance policy benefits
      const insurancePolicyBenefitIds = TreatmentPlanBenefit.InsurancePolicyBenefits.map(
        (benefit) => benefit.Id
      );

      // Get keys to remove from state
      const keysToRemove = Object.keys(state.entities)
        .filter((key) =>
          insurancePolicyBenefitIds.includes(
            state.entities[key].insurancePolicyBenefitId
          )
        )
        .map(Number);

      // Add new fees and remove old ones
      const newState = adapter.addMany(fees, state);
      return adapter.removeMany(keysToRemove, newState);
    }
  ),
  on(TreatmentPageActions.lineItemUpdated, (state, { entity }) => {
    const lineItem: LineItemModelType = {
      ...state.entities[entity.id],
      ...entity.changes,
    };
    if (lineItem.parent) {
      const updateOneState = adapter.updateOne(entity, state);
      const childIdsToSum = Object.keys(updateOneState.entities).filter((k) =>
        lineItem.parent.lineItems.map((l) => l.id).includes(+k)
      );
      const lineItemChildren = childIdsToSum.map(
        (c) => updateOneState.entities[c]
      ) as DhmoLineItem[];
      const newInsuranceBenefitTotal = lineItemChildren.reduce(
        (pv, cv) => pv + cv.insuranceBenefit,
        0
      );
      const newPatientCopayTotal = lineItemChildren.reduce(
        (pv, cv) => pv + cv.patientCopay,
        0
      );
      const childIdx = lineItem.parent.lineItems.findIndex(
        (l) => l.id === lineItem.id
      );
      return adapter.updateOne(
        {
          id: lineItem.parent.id,
          changes: {
            insuranceBenefit: newInsuranceBenefitTotal,
            patientCopay: newPatientCopayTotal,
            lineItems: [
              ...lineItem.parent.lineItems.slice(0, childIdx),
              { ...lineItem.parent.lineItems[childIdx], ...entity.changes },
              ...lineItem.parent.lineItems.slice(childIdx + 1),
            ],
          },
        },
        updateOneState
      );
    }
    return adapter.updateOne(entity, state);
  })
);
